import { Routes, Route, Navigate } from 'react-router-dom';
import SchoolSelector from './components/SchoolSelector/SchoolSelector';
import AppLayout from './layouts/AppLayout/AppLayout';
import DashboardRoute from './pages/Dashboard/DashboardRoute';
import SchoolYearView from './pages/SchoolYearView/SchoolYearView';
import FamiliesList from './pages/FamiliesList/FamiliesList';
import FamilyDetail from './pages/FamilyDetail/FamilyDetail';
import SignedEnrollmentPacket from './pages/SignedEnrollmentPacket/SignedEnrollmentPacket';
import StudentDetail from './pages/StudentDetail/StudentDetail';
import StudentRoute from './pages/StudentDetail/StudentRoute';
import ParentHousehold from './pages/ParentHousehold/ParentHousehold';
import ParentEnrollment from './pages/ParentEnrollment/ParentEnrollment';
import EnrollChecklist from './pages/EnrollChecklist/EnrollChecklist';
import EnrollStep from './pages/EnrollChecklist/EnrollStep';
import SettingsLayout from './pages/SchoolSettings/SettingsLayout';
import SettingsIndexRoute from './pages/SchoolSettings/SettingsIndexRoute';
import SettingsSectionRoute from './pages/SchoolSettings/SettingsSectionRoute';
import Enrollments from './pages/Enrollments/Enrollments';
import SchoolInvoices from './pages/SchoolInvoices/SchoolInvoices';
import Courses from './pages/Courses/Courses';
import CourseDetail from './pages/Courses/CourseDetail';
import ClassDetail from './pages/Courses/ClassDetail';
import ClassPeoplePage from './pages/Courses/ClassSections/ClassPeoplePage';
import ClassAssignmentsPage from './pages/Courses/ClassSections/ClassAssignmentsPage';
import ClassGradesPage from './pages/Courses/ClassSections/ClassGradesPage';
import ClassAttendancePage from './pages/Courses/ClassSections/ClassAttendancePage';
import ClassStandardsPage from './pages/Courses/ClassSections/ClassStandardsPage';
import ClassSchedulePage from './pages/Courses/ClassSections/ClassSchedulePage';
import { CLASS_SECTIONS, TEACHING_SECTIONS } from './components/Sidebar/contextNav';
import Staff from './pages/Staff/Staff';
import StaffMemberDetail from './pages/Staff/StaffMemberDetail';
import Teaching from './pages/Teaching/Teaching';
import TeachingOverview from './pages/Teaching/TeachingOverview';
import TeachingCategoryPage from './pages/Teaching/TeachingCategoryPage';
import TeachingSchedulePage from './pages/Teaching/TeachingSchedulePage';
import OperationalSchedule from './pages/Schedules/OperationalSchedule';
import AttendanceRoll from './pages/Schedules/AttendanceRoll';
import AttendanceDay from './pages/Schedules/AttendanceDay';
import AttendanceOverview from './pages/Schedules/AttendanceOverview';
import ViewAsFamily from './pages/ViewAsFamily/ViewAsFamily';
import ParentDashboard from './pages/ParentDashboard/ParentDashboard';
import ParentStudentPage from './pages/ParentStudent/ParentStudentPage';
import { MyInvoices } from './pages/MyInvoices/MyInvoices';
import { AppIndexRedirect } from './components/AppIndexRedirect';
import ProtectedRoute from './components/ProtectedRoute';
import dashboardConfig from './pages/Dashboard/Dashboard.config';
import schoolYearViewConfig from './pages/SchoolYearView/SchoolYearView.config';
import familiesListConfig from './pages/FamiliesList/FamiliesList.config';
import type { PageConfig } from './types';

// Parent Portal
import ReenrollmentForm from './pages/ParentPortal/ReenrollmentForm';
import EnrollmentPacket from './pages/ParentPortal/EnrollmentPacket';
import InquiryForm from './pages/ParentPortal/InquiryForm';
import AdmissionsForm from './pages/ParentPortal/AdmissionsForm';

interface RouteConfigChild {
  path: string;
  component: React.ComponentType;
  config: PageConfig | null;
}

interface RouteConfigEntry {
  path: string;
  component?: React.ComponentType;
  layout?: React.ComponentType;
  config?: PageConfig | null;
  children?: RouteConfigChild[];
}

export const routeConfig: RouteConfigEntry[] = [
  {
    path: '/app',
    component: SchoolSelector,
    config: null,
  },
  {
    path: '/app/:schoolSlug',
    layout: AppLayout,
    children: [
      {
        path: 'dashboard',
        component: DashboardRoute,
        config: dashboardConfig,
      },
      {
        path: 'years/:yearSlug',
        component: SchoolYearView,
        config: schoolYearViewConfig,
      },
      {
        path: 'years/:yearSlug/schedule',
        component: OperationalSchedule,
        config: null,
      },
      {
        path: 'years/:yearSlug/attendance',
        component: AttendanceDay,
        config: null,
      },
      {
        path: 'years/:yearSlug/attendance/:scheduleBlockId',
        component: AttendanceRoll,
        config: null,
      },
      {
        path: 'years/:yearSlug/attendance-report',
        component: AttendanceOverview,
        config: null,
      },
      {
        path: 'families',
        component: FamiliesList,
        config: familiesListConfig,
      },
      {
        path: 'settings',
        component: SettingsLayout,
        config: null,
      },
      {
        path: 'settings/:section',
        component: SettingsSectionRoute,
        config: null,
      },
    ],
  },
];

export function renderRoutes() {
  return (
    <Routes>
      <Route path="/app" element={<SchoolSelector />} />
      <Route path="/app/:schoolSlug" element={<AppLayout />}>
        <Route index element={<AppIndexRedirect />} />
        <Route
          path="dashboard"
          element={
            <ProtectedRoute>
              <DashboardRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug"
          element={
            <ProtectedRoute>
              <SchoolYearView />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/enrollments"
          element={
            <ProtectedRoute>
              <Enrollments />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/invoices"
          element={
            <ProtectedRoute>
              <SchoolInvoices />
            </ProtectedRoute>
          }
        />
        {/* A family's year: its checklist, and each step on it. */}
        <Route
          path="years/:yearSlug/enroll"
          element={
            <ProtectedRoute>
              <EnrollChecklist />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/enroll/:itemId"
          element={
            <ProtectedRoute>
              <EnrollStep />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/courses"
          element={
            <ProtectedRoute>
              <Courses />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/courses/:courseSlug"
          element={
            <ProtectedRoute>
              <CourseDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/courses/:courseSlug/classes/:classOfferingId"
          element={
            <ProtectedRoute>
              <ClassDetail />
            </ProtectedRoute>
          }
        >
          <Route index element={<Navigate to={CLASS_SECTIONS[0].path} replace />} />
          <Route path="people" element={<ClassPeoplePage />} />
          <Route path="assignments" element={<ClassAssignmentsPage />} />
          <Route path="grades" element={<ClassGradesPage />} />
          <Route path="attendance" element={<ClassAttendancePage />} />
          <Route path="standards" element={<ClassStandardsPage />} />
          <Route path="schedule" element={<ClassSchedulePage />} />
        </Route>
        <Route
          path="years/:yearSlug/teaching"
          element={
            <ProtectedRoute>
              <Teaching />
            </ProtectedRoute>
          }
        >
          <Route index element={<TeachingOverview />} />
          <Route path="schedule" element={<TeachingSchedulePage />} />
          {TEACHING_SECTIONS.filter((section) => section.path && section.key !== 'schedule').map((section) => (
            <Route
              key={section.key}
              path={section.path}
              element={<TeachingCategoryPage section={section.key} />}
            />
          ))}
        </Route>
        <Route
          path="years/:yearSlug/schedule"
          element={
            <ProtectedRoute>
              <OperationalSchedule />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/attendance"
          element={
            <ProtectedRoute>
              <AttendanceDay />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/attendance/:scheduleBlockId"
          element={
            <ProtectedRoute>
              <AttendanceRoll />
            </ProtectedRoute>
          }
        />
        <Route
          path="years/:yearSlug/attendance-report"
          element={
            <ProtectedRoute>
              <AttendanceOverview />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff"
          element={
            <ProtectedRoute>
              <Staff />
            </ProtectedRoute>
          }
        />
        <Route
          path="staff/:staffMemberId"
          element={
            <ProtectedRoute>
              <StaffMemberDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="household"
          element={
            <ProtectedRoute>
              <ParentHousehold />
            </ProtectedRoute>
          }
        />
        {/* The old enrollment page: goes to the selected year's enroll page. */}
        <Route
          path="enrollment"
          element={
            <ProtectedRoute>
              <ParentEnrollment />
            </ProtectedRoute>
          }
        />
        {/* The old single page; anything still pointing here lands on the record. */}
        <Route path="family" element={<Navigate to="../household" replace />} />
        <Route
          path="invoices"
          element={
            <ProtectedRoute>
              <MyInvoices />
            </ProtectedRoute>
          }
        />
        {/*
          An admin viewing a family gets the family's own pages, not a page of
          their own — see ViewAsFamily.
        */}
        <Route
          path="view-as-family/:familyId"
          element={
            <ProtectedRoute>
              <ViewAsFamily />
            </ProtectedRoute>
          }
        >
          <Route index element={<ParentDashboard />} />
          <Route path="household" element={<ParentHousehold />} />
          <Route path="enrollment" element={<ParentEnrollment />} />
          <Route path="years/:yearSlug/enroll" element={<EnrollChecklist />} />
          <Route path="years/:yearSlug/enroll/:itemId" element={<EnrollStep />} />
          <Route path="invoices" element={<MyInvoices />} />
          <Route path="students/:personId" element={<ParentStudentPage />} />
          <Route path="family" element={<Navigate to="../household" replace />} />
        </Route>
        <Route
          path="families"
          element={
            <ProtectedRoute>
              <FamiliesList />
            </ProtectedRoute>
          }
        />
        <Route
          path="families/:familySlug"
          element={
            <ProtectedRoute>
              <FamilyDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="families/:familySlug/enrollment"
          element={
            <ProtectedRoute>
              <FamilyDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="families/:familySlug/billing"
          element={
            <ProtectedRoute>
              <FamilyDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="families/:familySlug/enrollment-packet/:familyEnrollmentId"
          element={
            <ProtectedRoute>
              <SignedEnrollmentPacket mode="packet" />
            </ProtectedRoute>
          }
        />
        <Route
          path="families/:familySlug/tuition-agreement/:familyEnrollmentId"
          element={
            <ProtectedRoute>
              <SignedEnrollmentPacket mode="agreement" />
            </ProtectedRoute>
          }
        />
        <Route
          path="students/:personId"
          element={
            <ProtectedRoute>
              <StudentRoute />
            </ProtectedRoute>
          }
        />
        <Route
          path="students/:personId/enrollment"
          element={
            <ProtectedRoute>
              <StudentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="students/:personId/grades"
          element={
            <ProtectedRoute>
              <StudentDetail />
            </ProtectedRoute>
          }
        />
        <Route
          path="settings"
          element={
            <ProtectedRoute>
              <SettingsLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<SettingsIndexRoute />} />
          <Route path=":section" element={<SettingsSectionRoute />} />
        </Route>
      </Route>
      {/* Parent Portal Routes - Token-based access, no authentication required */}
      <Route path="/app/enroll/:token" element={<ReenrollmentForm />} />
      <Route path="/app/packet/:token" element={<EnrollmentPacket />} />
      <Route path="/app/inquire/:token" element={<InquiryForm />} />
      <Route path="/app/apply/:token" element={<AdmissionsForm />} />
      <Route path="/app/admit/:token" element={<AdmissionsForm />} />

      <Route path="*" element={<Navigate to="/app" replace />} />
    </Routes>
  );
}
